/**
 * TenderTimeline — chronologie des events d'un tender (pipeline + comments +
 * corrections + erreurs).
 *
 * Chargée on-demand (pas sur le rendu principal) via un panneau dépliable
 * dans TenderDetail pour éviter le coût initial.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Cog, MessageSquare, Pencil, AlertTriangle, type LucideIcon } from 'lucide-react';
import { api } from '../lib/api-client';

type TimelineEvent = {
  kind: 'pipeline' | 'comment' | 'correction' | 'error';
  at: number;
  actor: string | null;
  title: string;
  detail?: string;
  meta?: Record<string, unknown>;
};

type Filter = 'all' | 'pipeline' | 'comment' | 'correction' | 'error';

export function TenderTimeline({ tenderId }: { tenderId: string }) {
  const [events, setEvents] = useState<TimelineEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    api
      .get<{ events: TimelineEvent[] }>(`/api/tenders/${tenderId}/timeline`)
      .then((d) => setEvents(d.events))
      .catch((e) => setError(e instanceof Error ? e.message : 'Erreur'));
  }, [tenderId]);

  if (error) return <div className="text-sm text-red-600">Erreur : {error}</div>;
  if (!events) return <div className="text-sm text-gray-500">Chargement…</div>;

  const visible = filter === 'all' ? events : events.filter((e) => e.kind === filter);

  const counts = {
    pipeline: events.filter((e) => e.kind === 'pipeline').length,
    comment: events.filter((e) => e.kind === 'comment').length,
    correction: events.filter((e) => e.kind === 'correction').length,
    error: events.filter((e) => e.kind === 'error').length,
  };

  return (
    <div>
      <div className="flex gap-2 mb-3 flex-wrap text-xs">
        <FilterChip value="all" current={filter} onClick={setFilter}>
          Tous ({events.length})
        </FilterChip>
        <FilterChip value="pipeline" current={filter} onClick={setFilter} icon={Cog}>
          Pipeline ({counts.pipeline})
        </FilterChip>
        <FilterChip value="comment" current={filter} onClick={setFilter} icon={MessageSquare}>
          Commentaires ({counts.comment})
        </FilterChip>
        <FilterChip value="correction" current={filter} onClick={setFilter} icon={Pencil}>
          Corrections ({counts.correction})
        </FilterChip>
        <FilterChip value="error" current={filter} onClick={setFilter} icon={AlertTriangle}>
          Erreurs ({counts.error})
        </FilterChip>
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-gray-500">Aucun event.</p>
      ) : (
        <ol className="relative border-l-2 border-gray-200 dark:border-gray-700 ml-3">
          {visible.map((e, idx) => (
            <li key={idx} className="mb-4 ml-4">
              <span
                className={`absolute -left-[9px] w-4 h-4 rounded-full border-2 border-white dark:border-gray-900 ${bulletColor(e.kind)}`}
              />
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {(() => {
                      const Icon = iconFor(e.kind);
                      return <Icon aria-hidden className="w-4 h-4 shrink-0" strokeWidth={1.75} />;
                    })()}
                    <span>{e.title}</span>
                  </div>
                  {e.detail && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 whitespace-pre-wrap break-words">
                      {e.detail.length > 300 ? e.detail.slice(0, 300) + '…' : e.detail}
                    </div>
                  )}
                  {e.meta && e.meta.duration_ms != null && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      <span>⏱ {String(e.meta.duration_ms)} ms</span>
                    </div>
                  )}
                </div>
                <div className="text-right text-xs text-gray-500 flex-shrink-0">
                  {e.actor && <div>{e.actor.split('@')[0]}</div>}
                  <div>{new Date(e.at).toLocaleString('fr-FR')}</div>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function FilterChip({
  value,
  current,
  onClick,
  children,
  icon: Icon,
}: {
  value: Filter;
  current: Filter;
  onClick: (v: Filter) => void;
  children: ReactNode;
  icon?: LucideIcon;
}) {
  const active = value === current;
  return (
    <button
      onClick={() => onClick(value)}
      className={`px-2.5 py-1 rounded inline-flex items-center gap-1.5 ${
        active
          ? 'bg-primary-500 text-white'
          : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
      }`}
    >
      {Icon && <Icon aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />}
      <span>{children}</span>
    </button>
  );
}

function iconFor(kind: TimelineEvent['kind']): LucideIcon {
  switch (kind) {
    case 'pipeline':
      return Cog;
    case 'comment':
      return MessageSquare;
    case 'correction':
      return Pencil;
    case 'error':
      return AlertTriangle;
  }
}

function bulletColor(kind: TimelineEvent['kind']): string {
  switch (kind) {
    case 'pipeline':
      return 'bg-blue-500';
    case 'comment':
      return 'bg-gray-400';
    case 'correction':
      return 'bg-orange-500';
    case 'error':
      return 'bg-red-500';
  }
}
